import { get, memo as memoize } from 'radash'

import { BASE_FONT_SIZE, SPACING_MULTIPLIER, Mode } from './constants'
import type {
  ThemeBreakpoints,
  ThemeFontFamily,
  ThemeFontSize,
  ThemeFontWeight,
  ThemeInterface,
  ThemeInterfaceKeys,
  ThemeRadius,
  ThemeColor,
} from './theme'

const PLANES = {
  base: 0,
  floating: 1,
  under: -1,
  overlay: 100,
  dialog: 200,
  debug: 1000,
}

export type Plane = keyof typeof PLANES

export const SPACINGS = [
  0, // 0px
  0.25, // 1px
  0.5, // 2px
  1, // 4px
  1.5, // 6px
  2, // 8px
  2.5, // 10px
  3, // 12px
  3.5, // 14px
  4, // 16px
  5, // 20px
  6, // 24px
  7, // 28px
  8, // 32px
  9, // 36px
  10, // 40px
  11, // 44px
  12, // 48px
  14, // 56px
  16, // 64px
  18, // 72px
  20, // 80px
  24, // 96px
  28, // 112px
  32, // 128px
] as const

export type Spacing = (typeof SPACINGS)[number]

export const pxToRem = (px: number): string => `${px / BASE_FONT_SIZE}rem`

/**
 * This is the signature for functions like padding and margin
 * with multiple overloads that works like padding/margin and
 * makes it clear what each argument is.
 */
interface MultiSpaceFunction {
  /**
   * @param space - The space for all sides
   */
  (space: Spacing): string
  /**
   * @param topAndBottom - The space for top and bottom
   * @param leftAndRight - The space for left and right
   */
  (topAndBottom: Spacing, leftAndRight: Spacing): string
  /**
   * @param top - The space for top
   * @param leftAndRight - The space for left and right
   * @param bottom - The space for bottom
   */
  (top: Spacing, leftAndRight: Spacing, bottom: Spacing): string
  /**
   * @param top - The space for top
   * @param right - The space for right
   * @param bottom - The space for bottom
   * @param left - The space for left
   */
  (top: Spacing, right: Spacing, bottom: Spacing, left: Spacing): string
}

interface SpaceFunction {
  /**
   * This returns the rem suffix by default.
   * @param input - The space that will be multiplied by 4 and converted to rem
   */
  (input: Spacing): string
  /**
   * @param input - The space that will be multiplied by 4 and converted to rem
   * @param suffix - Whether or not to add the rem suffix
   */
  (input: Spacing, suffix: boolean): string
}

/**
 * Space utility that works with a multiple of 4 and returns a rem value.
 *
 * @see {@link padding} for padding
 */
export const space: SpaceFunction = memoize(
  (input: Spacing, suffix: boolean = true) => {
    const result = input * SPACING_MULTIPLIER
    return suffix ? pxToRem(result) : `${result}`
  },
  {
    key: (input: Spacing, suffix?: boolean) => `${input}${suffix ?? false}`,
  },
)

/**
 * Utility function for padding.
 * It's memoized as there's not point in re-calculating the same values over and over.
 *
 * @see {@link space} for setting `gap`
 */
export const padding: MultiSpaceFunction = memoize(
  (...inputs: Spacing[]): string =>
    inputs.map((input) => space(input, true)).join(' '),
  {
    // This is necessary so all of the arguments are considered
    key: (...args: Spacing[]) => args.join(' '),
  },
)

/** Alias for padding for semantically using it on margin */
export const margin = padding

type Props = { theme: ThemeInterface }

export const color =
  (themeColor: ThemeColor) =>
  ({ theme }: Props): string =>
    theme.color[themeColor] as string

export const fontFamily =
  (fontFamily: ThemeFontFamily) =>
  ({ theme }: Props): string =>
    theme.font.family[fontFamily]

export const fontWeight =
  (themeFontWeight: ThemeFontWeight) =>
  ({ theme }: Props): number =>
    theme.font.weight[themeFontWeight]

export const fontSize =
  (themeFontSize: ThemeFontSize) =>
  ({ theme }: Props): string =>
    theme.font.size[themeFontSize]

export const breakpoint =
  (size: ThemeBreakpoints) =>
  // Returns a props lookup function
  ({ theme: _theme }: Props): string =>
    `@media (min-width: ${_theme.breakpoints[size]})`

type ThemeRadiusWithNone = ThemeRadius | 'none'

/**
 * Signature for the Radius function with multiple overloads
 * allowing setup of radius for all corners, or individual corners
 */
interface RadiusFunction {
  /**
   * @param input - The radius for all corners
   */
  (input: ThemeRadius): (props: Props) => string
  /**
   * @param input - The radius for top and bottom, left and right
   */
  (
    input: `${ThemeRadiusWithNone} ${ThemeRadiusWithNone}`,
  ): (props: Props) => string
  /**
   * @param input - The radius for top, left and right, bottom
   */
  (
    input: `${ThemeRadiusWithNone} ${ThemeRadiusWithNone} ${ThemeRadiusWithNone}`,
  ): (props: Props) => string
  /**
   * @param input - The radius for top, right, bottom, left
   */
  (
    input: `${ThemeRadiusWithNone} ${ThemeRadiusWithNone} ${ThemeRadiusWithNone} ${ThemeRadiusWithNone}`,
  ): (props: Props) => string
}

/** Look up a radius from the theme provider */
export const borderRadius: RadiusFunction =
  (input) =>
  // Returns a props lookup function
  ({ theme: _theme }: Props): string => {
    const sizes = input.split(' ') as ThemeRadiusWithNone[]
    return sizes
      .map((size) => {
        if (size === 'none') return '0'
        return _theme.radius[size]
      })
      .join(' ')
  }

/**
 * Utility function for looking up theme values.
 */
export const theme = (keys: ThemeInterfaceKeys) => (props: any) =>
  get(props, `theme.${keys}`)

/**
 * Named planes for the z-index,
 * you can put multiple planes and they will be summed, like:
 * plane('modal', 'aboveFollowingContent')
 */
export const plane = memoize(
  (...planes: Plane[]): number => planes.reduce((acc, p) => acc + PLANES[p], 0),
  {
    key: (...planes) => planes.join(''),
  },
)

/**
 * Returns the preferred system color match
 */
export const getSystemColorSchemeMatch = (): MediaQueryList =>
  window.matchMedia('(prefers-color-scheme: dark)')

/**
 * Returns the preferred system color scheme
 */
export const getSystemColorScheme = (): Mode =>
  getSystemColorSchemeMatch().matches ? Mode.DARK : Mode.LIGHT
